import React, { Suspense } from "react";
import { Routes, Route, useParams, Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { getAllPosts, findPost } from "../data/blog";
import { posts } from "../resources/posts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

function BlogIndex() {
  const allPosts = getAllPosts();
  const canonical = "https://free1099calc.com/blog";
  
  // Merge blog posts with enhanced metadata from posts.ts
  const enrichedPosts = allPosts.map(post => {
    const enhanced = posts.find(p => p.slug === post.slug);
    return enhanced ? { ...post, ...enhanced } : post;
  });

  const sorted = enrichedPosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const categories = Array.from(new Set(sorted.map((p: any) => p.category).filter(Boolean)));

  return (
    <>
      <Helmet>
        <title>1099 Tax Guides & Resources for Freelancers | Free1099Calc</title>
        <meta
          name="description"
          content="Expert tax guides, deduction tips, and quarterly tax planning resources for 1099 contractors, freelancers, and self-employed professionals."
        />
        <meta name="keywords" content="1099 tax guides, freelance tax tips, contractor tax deductions, quarterly taxes, self-employment resources" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-2 bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 rounded-full mb-6">
              <span className="text-primary-300 text-sm font-semibold">📚 Tax Guides & Resources</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              1099 Tax Guides for Freelancers
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Practical guides for 1099 contractors, freelancers, and small business owners. Updated periodically with plain-English explanations and tips.
            </p>
          </div>
        </div>
      </div>

      <main className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Expense Tracker Banner */}
          <div className="mb-12">
            <ExpenseTrackerBanner compact />
          </div>

          {/* Category Filter Tabs */}
          {categories.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              <button className="px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors">
                All Posts
              </button>
              {categories.map(cat => (
                <button key={cat} className="px-4 py-2 rounded-lg bg-white text-slate-700 font-medium hover:bg-slate-50 border border-slate-200 transition-colors">
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Blog Posts Grid */}
          {sorted.length === 0 ? (
            <div className="rounded-2xl border bg-white p-6">
              <p className="text-slate-700">
                No posts yet. Check back soon for tax guides and resources!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sorted.map((post: any) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group block bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Thumbnail */}
                  {post.thumbnail ? (
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <img 
                        src={post.thumbnail} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {post.category && (
                        <div className="absolute top-3 left-3 px-3 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full">
                          {post.category}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                      {post.description}
                    </p>
                    
                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {post.readTime && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {post.readTime}
                          </span>
                        )}
                      </div>
                      <span className="text-primary-600 font-medium group-hover:gap-2 transition-all inline-flex items-center gap-1">
                        Read
                        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function BlogPost() {
  const { slug = "" } = useParams();
  const post = findPost(slug);

  if (!post) {
    return (
      <main className="mx-auto max-w-3xl px-4 md:px-6 py-8">
        <Helmet>
          <title>Not found | Free1099Calc</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="rounded-2xl border bg-white p-6">
          <h1 className="text-2xl font-semibold">Post not found</h1>
          <p className="mt-2 text-slate-700">
            We couldn’t find that article. Browse the{" "}
            <Link to="/blog" className="underline">blog index</Link>.
          </p>
        </div>
      </main>
    );
  }

  const { Component, title, description, date, slug: s } = post;
  const canonical = `https://free1099calc.com/blog/${s}`;

  return (
    <main className="mx-auto max-w-3xl px-4 md:px-6 py-8">
      <Helmet>
        <title>{title} | Free1099Calc</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <Suspense fallback={<div className="rounded-2xl border bg-white p-6">Loading…</div>}>
        <Component />
      </Suspense>
    </main>
  );
}

export default function ResourcesRouter() {
  return (
    <Routes>
      <Route index element={<BlogIndex />} />
      <Route path=":slug" element={<BlogPost />} />
    </Routes>
  );
}
